import {
  Badge,
  Flex,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
} from "@chakra-ui/react"
import { useQuery } from "@tanstack/react-query"
import { useNavigate } from "@tanstack/react-router"
import { useEffect, useMemo } from "react"
import { WareHouseService, type WarehousesDTO } from "../../../client"
import { useWarehouseInventoryBlocks } from "../../../hooks/useWarehouseInventoryBlocks"
import { PaginationFooter } from "../../Common/PaginationFooter.tsx"
import {
  RowCheckbox,
  SelectAllCheckbox,
  useRowSelection,
} from "../selection.tsx"
import { InventoryBlockReadNotice } from "../shared/InventoryBlockReadNotice"
import { getPaginatedItems } from "../shared/pagination"
import { WmsTableListCard } from "../shared/WmsTableListCard"
import { universalSearch } from "../utils"

type Props = {
  searchQuery?: string
  includeArchived?: boolean
  page: number
  pageSize: number
  onChangePage: (page: number) => void
  // Колбек для передачі вибраних позицій нагору (для друку тощо)
  onSelectionChange?: (
    selectedItems: WarehousesDTO[],
    selectedIds: string[],
  ) => void
}

export function WarehousesList({
  searchQuery,
  includeArchived,
  page,
  pageSize,
  onChangePage,
  onSelectionChange,
}: Props) {
  const navigate = useNavigate()
  const hoverBg = useColorModeValue("gray.50", "whiteAlpha.100")
  const theadBg = useColorModeValue("gray.100", "gray.700")
  const mutedText = useColorModeValue("gray.500", "gray.400")
  const zebraBg = useColorModeValue("blackAlpha.50", "whiteAlpha.50")

  const { data, isPending, isError, error } = useQuery<WarehousesDTO[]>({
    queryKey: [
      "wms",
      "warehouses",
      "list",
      { includeArchived: !!includeArchived },
    ],
    queryFn: () => WareHouseService.getAllWarehouses(includeArchived),
  })
  const { blockedWarehouseIds: activeWarehouses } =
    useWarehouseInventoryBlocks()

  // Reusable вибір рядків через хук
  const selection = useRowSelection()

  // Повідомляємо батьківський компонент про вибрані позиції
  // Використовуємо усі завантажені items (не лише відфільтровані),
  // щоб зберігати вибір незалежно від поточного фільтру
  useEffect(() => {
    if (!onSelectionChange) return
    const selectedIds = selection.selectedIds
    const selectedItems: WarehousesDTO[] = (data ?? []).filter((i) =>
      selection.selected.has(i.id),
    )
    try {
      onSelectionChange(selectedItems, selectedIds)
    } catch (_) {}
  }, [selection.selected, selection.selectedIds, data, onSelectionChange])

  // Фактичні дані з бекенду (завжди визначені для стабільності хуків)
  const items = data ?? []

  const filtered: WarehousesDTO[] = universalSearch(items, searchQuery, {
    keys: ["code", "name"],
  })
  const paginated = getPaginatedItems(filtered, page, pageSize)
  const visibleItems = paginated.items
  const currentPage = paginated.safePage

  // Обчислення станів для чекбоксу в хедері відносно відфільтрованих елементів
  const visibleIds = useMemo(
    () => visibleItems.map((i) => i.id),
    [visibleItems],
  )
  // Стан хедера обчислюється всередині SelectAllCheckbox, тому тут додатково не потрібен

  useEffect(() => {
    if (currentPage === page) return
    onChangePage(currentPage)
  }, [currentPage, onChangePage, page])

  const fmt = (d?: string) => (d ? new Date(d).toLocaleString() : "—")

  return (
    <WmsTableListCard
      isLoading={isPending}
      error={isError ? error : undefined}
      errorMessage="Nie udało się pobrać magazynów"
      isEmpty={!isPending && !isError && filtered.length === 0}
      emptyMessage={
        items.length > 0 && searchQuery?.trim()
          ? `Brak wyników dla frazy: ${searchQuery.trim()}`
          : "Brak magazynów"
      }
      minEmptyHeight="38vh"
      footer={
        <Flex
          justify="space-between"
          align={{ base: "stretch", md: "center" }}
          direction={{ base: "column", md: "row" }}
          gap={3}
          px={4}
          py={4}
          flexShrink={0}
        >
          <Text fontSize="sm" color={mutedText}>
            {paginated.total > 0
              ? `Pokazano ${(currentPage - 1) * pageSize + 1}-${(currentPage - 1) * pageSize + visibleItems.length} z ${paginated.total}`
              : "Pokazano 0 z 0"}
          </Text>
          <PaginationFooter
            mt={0}
            onChangePage={onChangePage}
            page={currentPage}
            hasNextPage={paginated.hasNextPage}
            hasPreviousPage={paginated.hasPreviousPage}
          />
        </Flex>
      }
    >
      <InventoryBlockReadNotice />
      <TableContainer flex="1" minH={0} overflowY="auto">
        <Table size="sm" variant="simple" sx={{ tableLayout: "fixed" }}>
          <Thead bg={theadBg} position="sticky" top={0} zIndex={1}>
            <Tr>
              <Th w="40px" p={2}>
                <SelectAllCheckbox
                  visibleIds={visibleIds}
                  selection={selection}
                />
              </Th>
              <Th>Kod</Th>
              <Th>Nazwa</Th>
              <Th>Utworzono</Th>
              <Th>Autor</Th>
            </Tr>
          </Thead>
          <Tbody>
            {visibleItems.map((w, idx) => (
              <Tr
                key={w.id}
                bg={idx % 2 === 0 ? "transparent" : zebraBg}
                _hover={{ bg: hoverBg }}
                cursor="pointer"
                onClick={() =>
                  navigate({
                    to: "/WMS/warehouses/$warehouse", // Не змінювати!!!!
                    params: { warehouse: w.id },
                  })
                }
              >
                <Td
                  w="40px"
                  p={2}
                  onClick={(e) => {
                    e.stopPropagation()
                  }}
                >
                  <RowCheckbox id={w.id} selection={selection} />
                </Td>
                <Td>
                  <Flex align="center" gap={2} wrap="wrap">
                    <Badge
                      colorScheme={w.is_active ? "green" : "orange"}
                      borderRadius="md"
                      px={2}
                      py={1}
                    >
                      {w.code}
                    </Badge>
                    {activeWarehouses.has(w.id) ? (
                      <Badge colorScheme="red" borderRadius="md" px={2} py={1}>
                        Trwa inwentaryzacja
                      </Badge>
                    ) : null}
                  </Flex>
                </Td>
                <Td fontWeight="medium">{w.name}</Td>
                <Td>{fmt(w.created_at)}</Td>
                <Td color={mutedText}>
                  {(w as any).who_created_acronym ?? w.who_created_by_id}
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </TableContainer>
    </WmsTableListCard>
  )
}
